import { Box, Button, Card, Chip, IconButton, Rating, Stack, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useState } from 'react';
import type { PropertyResponse } from '@ota/api-client';

interface Props {
  property: PropertyResponse;
  onClick: () => void;
}

/**
 * MakeMyTrip-style horizontal listing card. Image on the left, details in
 * the middle, price + rating block on the right. Collapses to a vertical
 * stack on mobile.
 */
export function PropertyListingCard({ property, onClick }: Props) {
  const imageIds = property.imageIds ?? [];
  const price = property.startingPrice ?? 0;
  const taxesEstimate = Math.round(price * 0.12);
  // roomsAvailable is set only when the user supplied check-in & check-out
  // on the search — null otherwise (no date-based availability computed).
  // We render Sold Out ONLY when the backend told us 0 rooms remain.
  const soldOut = property.roomsAvailable === 0;

  const [activeIdx, setActiveIdx] = useState(0);
  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIdx((i) => (i === 0 ? imageIds.length - 1 : i - 1));
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIdx((i) => (i === imageIds.length - 1 ? 0 : i + 1));
  };

  return (
    <Card
      sx={{
        overflow: 'hidden',
        borderColor: 'divider',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        // Dim the whole card slightly when sold out — same pattern MMT uses.
        opacity: soldOut ? 0.78 : 1,
        '&:hover': soldOut
          ? {}
          : {
              transform: 'translateY(-1px)',
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.10)',
              borderColor: 'primary.light',
            },
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }}>
        {/* Image / Carousel */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', md: 280 },
            height: { xs: 220, md: 220 },
            flexShrink: 0,
            cursor: 'pointer',
            backgroundColor: 'background.subtle',
            overflow: 'hidden',
          }}
          onClick={onClick}
        >
          {imageIds.length > 0 ? (
            <Box
              component="img"
              src={`/api/media/${imageIds[activeIdx]}`}
              alt={property.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center"
              sx={{ width: '100%', height: '100%', color: 'text.disabled' }}>
              No photo
            </Stack>
          )}

          {/* Carousel controls — only when multiple images */}
          {imageIds.length > 1 && (
            <>
              <IconButton
                size="small"
                onClick={prev}
                sx={{
                  position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={next}
                sx={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>

              {/* Dot indicators */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  position: 'absolute', bottom: 7, left: '50%', transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                }}
              >
                {imageIds.map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: i === activeIdx ? 14 : 6,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: i === activeIdx ? '#fff' : 'rgba(255,255,255,0.55)',
                      transition: 'width 0.2s, background-color 0.2s',
                    }}
                  />
                ))}
              </Stack>
            </>
          )}

          {/* Photo count badge (single image only) */}
          {imageIds.length === 1 && (
            <Box
              sx={{
                position: 'absolute', bottom: 8, left: 8,
                bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                px: 1.25, py: 0.5, borderRadius: 1,
                display: 'flex', alignItems: 'center', gap: 0.5,
                fontSize: 12, fontWeight: 600,
              }}
            >
              1 Photo
            </Box>
          )}

          {/* Sold Out ribbon — top-left of the image, only when dates were
              supplied and the backend reported zero remaining rooms. */}
          {soldOut && (
            <Box
              sx={{
                position: 'absolute', top: 12, left: 0,
                bgcolor: 'error.main', color: '#fff',
                px: 1.5, py: 0.5,
                borderTopRightRadius: 4, borderBottomRightRadius: 4,
                fontSize: 12, fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.35)',
              }}
            >
              Sold out
            </Box>
          )}
        </Box>

        {/* Details */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          flex={1}
          divider={
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '1px', alignSelf: 'stretch',
                bgcolor: 'divider',
              }}
            />
          }
        >
          <Stack flex={1} sx={{ p: 2.5, minWidth: 0 }} spacing={1} onClick={onClick} style={{ cursor: 'pointer' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                {property.name}
              </Typography>
              {property.starRating != null && property.starRating > 0 && (
                <Rating value={property.starRating} max={5} readOnly size="small" />
              )}
              {property.propertyTypeCode && (
                <Chip label={property.propertyTypeCode} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main' }}>
              <LocationOnIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {property.area}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {property.city}
              </Typography>
            </Stack>

            {property.amenities.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {property.amenities.slice(0, 4).map((a) => (
                  <Chip key={a.code} label={a.label} size="small" variant="outlined"
                    sx={{ height: 22, fontSize: 11 }} />
                ))}
                {property.amenities.length > 4 && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    +{property.amenities.length - 4} more
                  </Typography>
                )}
              </Stack>
            )}

            {property.description && (
              <Typography variant="body2" color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mt: 0.5,
                }}
              >
                {property.description}
              </Typography>
            )}
          </Stack>

          {/* Price block */}
          <Stack
            sx={{
              p: 2.5,
              minWidth: { md: 200 },
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Stack direction={{ xs: 'row', md: 'column' }} alignItems={{ xs: 'center', md: 'flex-end' }} spacing={1}>
              {property.avgRating != null && property.reviewCount > 0 ? (
                <RatingBadge avg={property.avgRating} count={property.reviewCount} />
              ) : (
                <Chip
                  size="small" label="No reviews yet"
                  sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                />
              )}
            </Stack>

            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.25}>
              {price > 0 && (
                <>
                  <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.1 }}>
                    ₹ {Number(price).toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    + ₹ {taxesEstimate.toLocaleString('en-IN')} taxes &amp; fees
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Per night
                  </Typography>
                </>
              )}
              {price === 0 && (
                <Typography variant="body2" color="text.disabled">
                  No rooms set up yet
                </Typography>
              )}
              <Button
                onClick={onClick}
                variant="contained"
                color={soldOut ? 'inherit' : 'secondary'}
                size="small"
                sx={{ mt: 1, alignSelf: { xs: 'flex-start', md: 'flex-end' } }}
                disabled={price === 0 || soldOut}
              >
                {soldOut ? 'Sold out' : price === 0 ? 'View details' : 'Book now'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * Color-coded rating badge à la MMT — green for excellent, blue for very
 * good, orange for good, red for bad.
 */
function RatingBadge({ avg, count }: { avg: number; count: number }) {
  const color =
    avg >= 4.5 ? 'success.main'
    : avg >= 4.0 ? 'success.light'
    : avg >= 3.5 ? 'info.main'
    : avg >= 2.5 ? 'warning.main'
    : 'error.main';

  const label =
    avg >= 4.5 ? 'Excellent'
    : avg >= 4.0 ? 'Very Good'
    : avg >= 3.5 ? 'Good'
    : avg >= 2.5 ? 'Fair'
    : 'Poor';

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Stack alignItems="flex-end">
        <Typography variant="body2" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({count} review{count === 1 ? '' : 's'})
        </Typography>
      </Stack>
      <Box
        sx={{
          bgcolor: color, color: '#fff',
          px: 1, py: 0.5, borderRadius: 1,
          fontSize: '0.95rem', fontWeight: 700,
          minWidth: 38, textAlign: 'center',
        }}
      >
        {avg.toFixed(1)}
      </Box>
    </Stack>
  );
}
